import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import { FieldNames } from '../case-detail/_case.lang';
import classnames from 'classnames';
import { commonEnums } from '@codametrix/ui-common';
import './styles/index.scss';
export type CaseEvidenceTable2Props = {
  techTransaction: CMxCommonApp.TechTransaction;
  chargeSessionCode: CMxCommonApp.ChargeSessionCode;
  onFeatureSelection: (selection: CMxCommonApp.ChargeSessionSelection) => void;
  panelActivations?: CMxCommonApp.PanelActivations;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const evidenceVerb = {
  partial: 'contains',
  complete: 'is',
  mapping: 'is'
};

const NoOpFeatureSelection = (
  feature: CMxCommonApp.ChargeSessionSelection
) => {};

class CaseEvidenceTable2 extends React.Component<CaseEvidenceTable2Props, any> {
  static displayName = 'CaseEvidenceTable2';
  static defaultProps = {
    onFeatureSelection: NoOpFeatureSelection,
    panelActivations: {},
    techTransaction: {}
  };

  constructor(props: CaseEvidenceTable2Props) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(ev: React.MouseEvent<HTMLUListElement>) {
    ev.preventDefault();
    const target =
      (ev.target as HTMLSpanElement) || document.createElement('span');

    const valuable = target.classList.contains('field-value')
      ? target
      : target.closest('.field-value');
    const linkable = target?.closest(
      '.case-evidence-item.case-evidence-linked'
    );
    if (!valuable || !linkable) return;

    const item = linkable.attributes.getNamedItem('data-index');
    if (!item) return;

    const { chargeSessionCode } = this.props;
    const currentIndex = parseInt(item.value);
    const evidence = chargeSessionCode.evidence[currentIndex];

    if (evidence) {
      const display = !linkable.classList.contains('case-evidence-active');

      const selectionState: CMxCommonApp.ChargeSessionEvidenceSelectionState = {
        active: display,
        hover: false,
        visible: true
      };
      this.props.onFeatureSelection({
        chargeSessionCode: chargeSessionCode,
        evidence: [evidence],
        selectionState
      });
    }
  }

  render(): React.ReactNode {
    const { chargeSessionCode, panelActivations, techTransaction } = this.props;
    const { evidence } = chargeSessionCode;

    const evidenceDisplay = evidence.map((evida, idx) => {
      const isActive = panelActivations[evida.hash]?.active ?? false;
      const styleClasses = [
        `case-evidence-item`,
        {
          'case-evidence-linked': evida.linkable,
          'case-evidence-active': isActive,
          'sentence-highlight':
            evida.featureType === commonEnums.EvidenceType.PARTIAL,
          'categorical-highlight':
            evida.featureType === commonEnums.EvidenceType.COMPLETE
        }
      ];
      const valueClasses = [
        `field-value`,
        'highlighted-text',
        'sensitive-data'
      ];

      const fieldName = FieldNames[evida.fieldName] || evida.fieldName;
      const verb = evidenceVerb[evida.featureType] || evida.featureType;
      let content: any = (evida.meta?.content ?? 'N/A').trim();

      if (evida.linkable) {
        content = <a href={`${evida.identifier}`}>{content}</a>;
      }

      return (
        <li className={classnames(styleClasses)} key={idx} data-index={idx}>
          The <span className="field-name">{fieldName}</span> {verb}{' '}
          <span className={classnames(valueClasses)}>{content}</span>
        </li>
      );
    });

    const codeType = chargeSessionCode.codeType.toLowerCase();
    const classes = [
      'case-evidence-table',
      'case-evidence-table2',
      'col-artifacts',
      `case-evidence-${codeType}`
    ];

    return (
      <div className={classnames(classes)}>
        {(techTransaction.hasProCode || techTransaction.hasTechCode) && (
          <div>
            <h5>BILLING DETAILS</h5>
            <ul className="case-evidence-list">
              <li>
                <span className="qa-empty-message empty-item-message tech-item">
                  {(techTransaction.isProCodeActive ||
                    techTransaction.isTechCodeActive) && (
                    <div className="case-evidence-item">
                      This {codeType === 'procedure' ? 'charge' : 'modifier'}{' '}
                      will be submitted{' '}
                      {codeType === 'procedure' ? 'as' : 'with'}{' '}
                      <b>
                        {techTransaction.isProCodeActive &&
                          techTransaction.proCode}
                      </b>
                      {techTransaction.isProCodeActive &&
                        techTransaction.isTechCodeActive &&
                        ' and '}
                      <b>
                        {techTransaction.isTechCodeActive &&
                          techTransaction.techCode}
                      </b>
                    </div>
                  )}

                  {!techTransaction.isProCodeActive &&
                    !techTransaction.isTechCodeActive && (
                      <div className="case-evidence-item">
                        The charge will not be submitted without a billing
                        designation.
                      </div>
                    )}
                </span>
              </li>
            </ul>
          </div>
        )}

        {evidence.length > 0 ? (
          <div>
            <h5>WHY WE SELECTED {chargeSessionCode.code}</h5>
            <ol onClick={this.handleClick} className="case-evidence-list">
              {evidenceDisplay}
            </ol>
          </div>
        ) : (
          <div>
            <h5>NO EVIDENCE AT THE MOMENT</h5>
            <ul className="case-evidence-list">
              <li>
                <span className="qa-empty-message empty-item-message no-evidence">
                  There is no displayable evidence at this time.
                </span>
              </li>
            </ul>
          </div>
        )}
      </div>
    );
  }
}

export { CaseEvidenceTable2 };
